import axios from 'axios';
import normalize from 'jsonapi-normalizer';
import store from 'store';

import { isNull, isUndefined } from 'lodash';
import apiBase from './api-base';

export const getMultipartHeaders = () => ({
  accept: 'application/json',
  'Content-Type': 'multipart/form-data',
  'X-LIFETALES-CLIENT': 'web-babys-first-year',
});

export const getJsonHeaders = () => ({
  accept: 'application/json',
  'Content-Type': 'application/json',
  'X-LIFETALES-CLIENT': 'web-babys-first-year',
});

export const getAuthorizedHeaders = () => {
  const token = store.get('auth_token');
  const cleanToken = token
  ? token
      .replace(/[^\x00-\xFF]/g, '')  // ✅ Remove non ISO-8859-1 chars
      .replace(/^[""]|[""]$/g, '')   // ✅ Remove curly quotes
      .replace(/^"|"$/g, '')         // ✅ Remove straight quotes
      .trim()
  : '';
  return {
    Authorization: `Token ${cleanToken}`,
    ...getJsonHeaders(),
  };
};

export const getRequestOptions = () => ({
  method: 'GET',
  headers: getAuthorizedHeaders(),
});
export const normalizeAxiosResponse = (data, headers) => {
  if (!data) return data;

  const nextData = JSON.parse(data);

  if (nextData.errors) return data;

  if (nextData.data && nextData.data.links) {
    nextData.data.attributes.links = nextData.data.links;
  }

  if (nextData.included) {
    nextData.included = nextData.included.map(inc => {
      if (!inc.links) return inc;
      return {
        ...inc,
        attributes: {
          ...inc.attributes,
          links: inc.links,
        },
      };
    });
  }

  if (Array.isArray(nextData.data)) {
    nextData.data = nextData.data.map(item => {
      if (item.links) {
        return {
          ...item,
          attributes: {
            ...item.attributes,
            links: item.links,
          },
        };
      }
      return item;
    });
  }

  const normData = normalize(nextData);
  return { ...normData, initial: JSON.parse(data) };
};

export class AxiosPriorityQueue {
  constructor(concurrency = 6) {
    this.queue = [];
    this.running = 0;
    this.concurrency = concurrency;
  }

  add(fn, priority = 'normal') {
    return new Promise((resolve, reject) => {
      this.queue.push({
        fn,
        priority,
        resolve,
        reject,
        timestamp: Date.now(),
      });

      this.queue.sort((a, b) => {
        const order = { high: 0, normal: 1, low: 2 };
        if (order[a.priority] !== order[b.priority]) {
          return order[a.priority] - order[b.priority];
        }
        return a.timestamp - b.timestamp;
      });

      this.run();
    });
  }

  async run() {
    if (this.running >= this.concurrency || !this.queue.length) return;

    this.running++;
    const { fn, resolve, reject } = this.queue.shift();

    try {
      const result = await fn();
      resolve(result);
    } catch (err) {
      reject(err);
    } finally {
      this.running--;
      this.run(); // ✅ Process next item in queue
    }
  }
}

const requestQueue = new AxiosPriorityQueue(6);

export const getAxiosInstance = (opts = { normalize: true }) => {
  const transformResponse = (opts.normalize && [normalizeAxiosResponse]) || null;
  const baseURL = new URL(apiBase).origin;
  return axios.create({
    headers: getAuthorizedHeaders(),
    baseURL: `${baseURL}/api`,
    transformResponse,
  });
};

export const post = (url, body, opts) => {
  return requestQueue.add(
    () => {
      const ax = getAxiosInstance(opts);
      return ax.post(url, body);
    },
    'high'
  );
};


export const patch = (url, body, opts) => {
  return requestQueue.add(
    () => {
      const ax = getAxiosInstance(opts);
      return ax.patch(url, body);
    },
    'high'
  );
};

export const put = (url, file, config) => {
  return requestQueue.add(
    () => axios.put(url, file, {
      headers: { 'Content-Type': file.type },
      ...config
    }),
    'high'
  );
};

export const get = (url, opts) => {
  return requestQueue.add(
    () => {
      const ax = getAxiosInstance(opts);
      return ax.get(url);
    },
    'normal'
  );
};


export const getSWR = (url) => {
  return requestQueue.add(
    () => {
      const ax = getAxiosInstance({ normalize: false });
      return ax.get(url);
    },
    'low'
  );
};


export const destroy = (url) => {
  return requestQueue.add(
    () => {
      const baseURL = new URL(apiBase).origin;
      const ax = axios.create({
        headers: getAuthorizedHeaders(),
        baseURL: `${baseURL}/api`,
      });
      return ax.delete(url);
    },
    'high'
  );
};

// ─────────────────────────────────────────────
// Helpers — unchanged
// ─────────────────────────────────────────────
export const prepareAttrs = values =>
  Object.keys(values).reduce((acc, key) => {
    if (isUndefined(values[key]) || isNull(values[key])) return acc;
    return {
      ...acc,
      [key.replace('-noac', '')]: values[key],
    };
  }, {});

export const prepareErrors = error => {
  const { errors } = JSON.parse(error.response.data);
  if (!errors) return {};
  const formikStatus = errors.reduce(
    (acc, el) => ({
      ...acc,
      [el.field]: el.detail,
    }),
    {}
  );
  return formikStatus;
};